import { faEye, faFileUpload, faSpinner, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Input } from 'reactstrap';
import Swal from 'sweetalert2';
import { useAuth0 } from 'auth0/reactAuth0Spa';
import { checkLoginForError, formatBearerToken, sanitizeFilename } from 'Common.functions';
import { DepartmentContext } from 'DepartmentWrapper';
import { constructUrl, fetchDataAgnostic } from 'Services/dataApi';
import {
  createUploadOperationId,
  getReplacementStatus,
  getUploadOutcomeMessage,
  isCommittedUpload,
  reconcileCandidateFileUpload,
  shouldReconcileUploadError,
} from 'Services/candidateFileUpload';
import Loading from 'Body/Statuses/Loading';
import { toast } from 'react-toastify';
import { createChecklistUploadOutcome } from './replacementUploadHelpers';

const candidatePhotoDocumentTypePk = 'candidate_photo';

const getDocumentCellKey = (candidatePk, documentTypePk) => {
  return `${candidatePk}_${documentTypePk}`;
};

const isCandidatePhotoType = (documentTypePk) => {
  return documentTypePk === candidatePhotoDocumentTypePk;
};

const sortCandidates = (candidates) => {
  return [...candidates].sort((firstCandidate, secondCandidate) => {
    const firstLastName = `${firstCandidate.LastName || ''}`.toLowerCase();
    const secondLastName = `${secondCandidate.LastName || ''}`.toLowerCase();

    if (firstLastName !== secondLastName) {
      return firstLastName.localeCompare(secondLastName);
    }

    return `${firstCandidate.FirstName || ''}`.toLowerCase().localeCompare(`${secondCandidate.FirstName || ''}`.toLowerCase());
  });
};

const hasDocumentForType = (candidate, documentTypePk) => {
  if (isCandidatePhotoType(documentTypePk)) {
    return Boolean(candidate.PhotoUrl);
  }

  return (candidate.Documents || []).some((document) => {
    return `${document.fk_DocumentType}` === `${documentTypePk}`;
  });
};

const getDocumentForType = (candidate, documentTypePk) => {
  if (isCandidatePhotoType(documentTypePk)) {
    return candidate.PhotoUrl
      ? {
          FullUrl: candidate.PhotoUrl,
          Name: candidate.PhotoUrl.split('/').pop(),
          isCandidatePhoto: true,
        }
      : null;
  }

  return (candidate.Documents || []).find((document) => {
    return `${document.fk_DocumentType}` === `${documentTypePk}`;
  });
};

const getDocumentName = (document) => {
  if (!document?.Name) {
    return '';
  }

  return document.Name.split('-UUID-')[1] || document.Name;
};

const UploadOutcomeMessage = ({ uploadOutcome, onRefresh, onRetry }) => {
  if (!uploadOutcome) {
    return null;
  }

  return (
    <div
      role="alert"
      style={{ color: '#842029', fontSize: 12, marginTop: 6, textAlign: 'center' }}
    >
      <div>{uploadOutcome.message}</div>
      <Button
        color="secondary"
        onClick={(event) => {
          event.stopPropagation();
          onRefresh();
        }}
        outline
        size="sm"
        style={{ marginTop: 5 }}
        type="button"
      >
        Refresh status
      </Button>
      {uploadOutcome.canRetry ? (
        <Button
          color="danger"
          onClick={(event) => {
            event.stopPropagation();
            onRetry();
          }}
          outline
          size="sm"
          style={{ marginLeft: 5, marginTop: 5 }}
          type="button"
        >
          Retry
        </Button>
      ) : null}
    </div>
  );
};

const DocumentChecklistRow = ({
  candidate,
  attachedDocument,
  documentTypePk,
  documentTypeName,
  isCandidatePhoto,
  isJustAdded,
  isUploading,
  uploadOutcome,
  onRefresh,
  onRetry,
  onViewDocument,
  onUpload,
}) => {
  const candidateName = `${candidate.LastName || ''}${candidate.LastName && candidate.FirstName ? ', ' : ''}${candidate.FirstName || ''}`;
  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0 || acceptedFiles.length !== 1) {
        toast.error(isCandidatePhoto ? 'Select one JPG, JPEG, or PNG image.' : 'Select one PDF, JPG, JPEG, or PNG file.');
        return;
      }

      onUpload(candidate, documentTypePk, acceptedFiles[0]);
    },
    [candidate, documentTypePk, isCandidatePhoto, onUpload],
  );

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: isCandidatePhoto ? ['image/jpeg', 'image/jpg', 'image/png'] : ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
    disabled: isUploading,
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  const statusLabel = isJustAdded ? 'Just added' : attachedDocument ? 'Has document' : 'Missing';
  const statusColor = attachedDocument || isJustAdded ? '#198754' : '#A65E00';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(220px, 1fr) 160px minmax(220px, 280px)',
        gap: 10,
        padding: '9px 12px',
        borderTop: '1px solid #E3E7EB',
        alignItems: 'center',
        backgroundColor: isJustAdded ? '#DFF7E5' : attachedDocument ? '#F5FFF7' : '#FFF9F3',
      }}
    >
      <div style={{ fontWeight: 'bold' }}>{candidateName || 'Unnamed Candidate'}</div>
      <div style={{ color: statusColor, fontWeight: isJustAdded ? 'bold' : 'normal' }}>{statusLabel}</div>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {attachedDocument ? (
            <Button
              color="primary"
              onClick={() => {
                onViewDocument(candidate, attachedDocument, isCandidatePhoto);
              }}
              size="sm"
              title={`View ${documentTypeName}`}
            >
              <FontAwesomeIcon icon={faEye} style={{ marginRight: 6 }} />
              View
            </Button>
          ) : null}
          <div
            {...getRootProps()}
            style={{
              alignItems: 'center',
              border: `1px dashed ${isDragActive ? '#0D6EFD' : '#7D8A99'}`,
              borderRadius: 4,
              color: '#40546D',
              cursor: isUploading ? 'wait' : 'pointer',
              display: 'flex',
              flex: 1,
              fontSize: 12,
              justifyContent: 'center',
              minHeight: 34,
              padding: '4px 8px',
              textAlign: 'center',
            }}
          >
            <input {...getInputProps()} />
            {isUploading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 6 }} /> Uploading
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faFileUpload} style={{ marginRight: 6 }} />
                {attachedDocument ? `Replace ${isCandidatePhoto ? 'photo' : 'document'}` : `Drop ${documentTypeName} here or click to browse`}
              </>
            )}
          </div>
        </div>
        <UploadOutcomeMessage onRefresh={onRefresh} onRetry={onRetry} uploadOutcome={uploadOutcome} />
      </div>
    </div>
  );
};

const DocumentCoverageCell = ({
  attachedDocument,
  candidate,
  documentType,
  isUploading,
  uploadOutcome,
  onRefresh,
  onRetry,
  onUpload,
  onViewDocument,
}) => {
  const documentTypePk = documentType.pk_DocumentType;
  const isCandidatePhoto = isCandidatePhotoType(documentTypePk);

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0 || acceptedFiles.length !== 1) {
        toast.error(isCandidatePhoto ? 'Select one JPG, JPEG, or PNG image.' : 'Select one PDF, JPG, JPEG, or PNG file.');
        return;
      }

      onUpload(candidate, documentTypePk, acceptedFiles[0]);
    },
    [candidate, documentTypePk, isCandidatePhoto, onUpload],
  );

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: isCandidatePhoto ? ['image/jpeg', 'image/jpg', 'image/png'] : ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
    disabled: isUploading,
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  return (
    <div
      {...getRootProps()}
      style={{
        alignItems: 'center',
        backgroundColor: attachedDocument ? '#E4F6E8' : '#FFF1E2',
        border: `1px dashed ${isDragActive ? '#0D6EFD' : attachedDocument ? '#72B982' : '#D09045'}`,
        borderRadius: 4,
        color: attachedDocument ? '#146C2E' : '#A65E00',
        cursor: isUploading ? 'wait' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        fontSize: 12,
        fontWeight: 'bold',
        gap: 4,
        justifyContent: 'center',
        minHeight: 54,
        padding: '6px 8px',
      }}
    >
      <input {...getInputProps()} />
      {isUploading ? (
        <>
          <FontAwesomeIcon icon={faSpinner} spin /> Uploading
        </>
      ) : attachedDocument ? (
        <>
          <Button
            color="primary"
            onClick={(event) => {
              event.stopPropagation();
              onViewDocument(candidate, attachedDocument, isCandidatePhoto);
            }}
            size="sm"
            type="button"
          >
            <FontAwesomeIcon icon={faEye} style={{ marginRight: 5 }} /> View
          </Button>
          <span>Drop or click to replace</span>
        </>
      ) : (
        <>
          <FontAwesomeIcon icon={faFileUpload} />
          <span>Drop or click to upload</span>
        </>
      )}
      <UploadOutcomeMessage onRefresh={onRefresh} onRetry={onRetry} uploadOutcome={uploadOutcome} />
    </div>
  );
};

const DocumentChecklist = ({ documentTypes = [] }) => {
  const [candidates, setCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [documentFilter, setDocumentFilter] = useState('all');
  const [viewMode, setViewMode] = useState('checklist');
  const [missingSnapshotPks, setMissingSnapshotPks] = useState([]);
  const [recentlyUploadedPks, setRecentlyUploadedPks] = useState([]);
  const [uploadingDocumentKeys, setUploadingDocumentKeys] = useState([]);
  const [documentUploadOutcomes, setDocumentUploadOutcomes] = useState({});
  const activeUploadRequestsRef = useRef({});
  const isMountedRef = useRef(true);
  const currentUploadContextRef = useRef('');
  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const { department, season } = useContext(DepartmentContext) || {};
  const departmentPk = department?.pk_Department;
  const seasonPk = season?.pk_Season;
  const uploadContextKey = `${departmentPk || ''}_${seasonPk || ''}`;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    currentUploadContextRef.current = uploadContextKey;
    activeUploadRequestsRef.current = {};
    setDocumentUploadOutcomes({});
    setUploadingDocumentKeys([]);
  }, [uploadContextKey]);

  const visibleDocumentTypes = useMemo(() => {
    const activeDocumentTypes = documentTypes.filter((documentType) => {
      return documentType && documentType.pk_DocumentType != null && !documentType.Hide;
    });

    return [
      { DocumentTypeName: 'Candidate Photo', pk_DocumentType: candidatePhotoDocumentTypePk },
      ...activeDocumentTypes,
    ];
  }, [documentTypes]);

  const selectedDocumentTypeDetails = useMemo(() => {
    return visibleDocumentTypes.find((documentType) => {
      return `${documentType.pk_DocumentType}` === `${selectedDocumentType}`;
    });
  }, [selectedDocumentType, visibleDocumentTypes]);

  const getMissingCandidatePks = (documentTypePk, candidateList = candidates) => {
    if (!documentTypePk) {
      return [];
    }

    return candidateList
      .filter((candidate) => {
        return !hasDocumentForType(candidate, documentTypePk);
      })
      .map((candidate) => {
        return candidate.pk_Candidate;
      });
  };

  const getCandidatesWithDocuments = useCallback(({ preserveMissingSnapshot = false } = {}) => {
    if (!departmentPk || !seasonPk) {
      return;
    }

    setIsLoadingCandidates(true);
    getTokenSilently()
      .then((token) => {
        return fetchDataAgnostic(
          'candidate/candidates/documents',
          {
            pk_Department: departmentPk,
            pk_Season: seasonPk,
          },
          formatBearerToken(token),
        );
      })
      .then((response) => {
        const candidateResults = Array.isArray(response?.data) ? response.data : Object.values(response?.data || {});
        setCandidates(sortCandidates(candidateResults));
        if (!preserveMissingSnapshot) {
          setMissingSnapshotPks([]);
          setRecentlyUploadedPks([]);
        }
      })
      .catch((error) => {
        setCandidates([]);
        Swal.fire({
          title: 'Unable to Load Documents',
          icon: 'error',
          text: 'The candidate document list could not be loaded.',
        });
        checkLoginForError({ err: error, loginWithRedirect });
      })
      .finally(() => {
        setIsLoadingCandidates(false);
      });
  }, [departmentPk, getTokenSilently, loginWithRedirect, seasonPk]);

  useEffect(() => {
    getCandidatesWithDocuments();
  }, [getCandidatesWithDocuments]);

  const visibleCandidates = useMemo(() => {
    if (!selectedDocumentType) {
      return [];
    }

    if (documentFilter === 'missing' && missingSnapshotPks.length > 0) {
      const snapshotPks = new Set(missingSnapshotPks);
      return candidates.filter((candidate) => {
        return snapshotPks.has(candidate.pk_Candidate);
      });
    }

    return candidates.filter((candidate) => {
      const candidateHasDocument = hasDocumentForType(candidate, selectedDocumentType);

      if (documentFilter === 'missing') {
        return !candidateHasDocument;
      }

      if (documentFilter === 'present') {
        return candidateHasDocument;
      }

      return true;
    });
  }, [candidates, documentFilter, missingSnapshotPks, selectedDocumentType]);

  const coverageSummary = useMemo(() => {
    const completeCandidateCount = candidates.filter((candidate) => {
      return visibleDocumentTypes.every((documentType) => {
        return hasDocumentForType(candidate, documentType.pk_DocumentType);
      });
    }).length;

    return {
      completeCandidateCount,
      missingCandidateCount: candidates.length - completeCandidateCount,
    };
  }, [candidates, visibleDocumentTypes]);

  const resetFilterSnapshot = (nextFilter, nextDocumentType = selectedDocumentType) => {
    setRecentlyUploadedPks([]);
    if (nextFilter === 'missing') {
      setMissingSnapshotPks(getMissingCandidatePks(nextDocumentType));
      return;
    }

    setMissingSnapshotPks([]);
  };

  const uploadCandidateDocument = async (candidate, documentTypePk, file, retryOutcome = null) => {
    const documentCellKey = getDocumentCellKey(candidate.pk_Candidate, documentTypePk);
    const isCandidatePhoto = isCandidatePhotoType(documentTypePk);
    const requestContextKey = uploadContextKey;
    const requestId = `${documentCellKey}_${Date.now()}`;
    const clientOperationId = retryOutcome?.clientOperationId || createUploadOperationId();
    const existingDocument = getDocumentForType(candidate, documentTypePk);
    if (existingDocument && !retryOutcome) {
      const confirmation = await Swal.fire({
        title: `Replace ${isCandidatePhoto ? 'photo' : 'document'}?`,
        text: isCandidatePhoto
          ? `Replace the photo for ${candidate.LastName || 'this candidate'}?`
          : `Replace ${getDocumentName(existingDocument)} for ${candidate.LastName || 'this candidate'}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Replace',
        confirmButtonColor: '#0D6EFD',
      });

      if (!confirmation.isConfirmed && !confirmation.value) {
        return;
      }
    }

    if (activeUploadRequestsRef.current[documentCellKey]) {
      return;
    }

    setUploadingDocumentKeys((previousDocumentKeys) => {
      return [...new Set([...previousDocumentKeys, documentCellKey])];
    });
    activeUploadRequestsRef.current[documentCellKey] = requestId;
    setDocumentUploadOutcomes((previousOutcomes) => {
      const nextOutcomes = { ...previousOutcomes };
      delete nextOutcomes[documentCellKey];
      return nextOutcomes;
    });

    const isCurrentRequest = () => {
      return (
        isMountedRef.current &&
        requestContextKey === currentUploadContextRef.current &&
        activeUploadRequestsRef.current[documentCellKey] === requestId
      );
    };

    let token;
    try {
      token = await getTokenSilently();
      const formData = new FormData();
      const endpoint = isCandidatePhoto ? 'files/admin/candidate/photo' : 'files/candidate/document';
      const requestParams = isCandidatePhoto
        ? {
            mimeType: file.type,
            pk_Department: departmentPk,
            pk_Season: seasonPk,
            userId: candidate.pk_Candidate,
          }
        : {};

      if (isCandidatePhoto) {
        formData.append('File', file);
      } else {
        const uploadPayload = {
          name: sanitizeFilename(file.name),
          pk_Department: departmentPk,
          pk_Candidate: candidate.pk_Candidate,
          pk_DocumentType: documentTypePk,
        };
        formData.append('File', file, JSON.stringify(uploadPayload));
      }

      const uploadResult = await axios.post(constructUrl(endpoint), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...formatBearerToken(token),
          'x-rezrate-upload-operation-id': clientOperationId,
        },
        params: requestParams,
      });

      if (isCurrentRequest() && isCommittedUpload(uploadResult.data)) {
        setRecentlyUploadedPks((previousCandidatePks) => {
          return [...new Set([...previousCandidatePks, candidate.pk_Candidate])];
        });
        await getCandidatesWithDocuments({ preserveMissingSnapshot: true });
        toast.success(`${isCandidatePhoto ? 'Photo' : 'Document'} uploaded.`);
      } else if (isCurrentRequest()) {
        const replacementStatus = getReplacementStatus(uploadResult.data);
        setDocumentUploadOutcomes((previousOutcomes) => {
          return {
            ...previousOutcomes,
            [documentCellKey]: createChecklistUploadOutcome({
              clientOperationId,
              file,
              isCandidatePhoto,
              replacementStatus,
            }),
          };
        });
      }
    } catch (error) {
      if (isCurrentRequest()) {
        let replacementStatus = getReplacementStatus(error.response?.data);
        let isCommitted = false;

        if (shouldReconcileUploadError(error)) {
          try {
            const reconciliation = await reconcileCandidateFileUpload({
              clientOperationId,
              headers: formatBearerToken(token),
              isProfilePhoto: isCandidatePhoto,
              pk_Candidate: candidate.pk_Candidate,
              pk_Department: departmentPk,
              pk_DocumentType: documentTypePk,
            });
            isCommitted = reconciliation.committed === true;
            replacementStatus = isCommitted ? 'already_committed' : 'unknown_reconciliation_required';
          } catch (reconciliationError) {
            replacementStatus = 'unknown_reconciliation_required';
          }
        }

        if (isCommitted) {
          setRecentlyUploadedPks((previousCandidatePks) => {
            return [...new Set([...previousCandidatePks, candidate.pk_Candidate])];
          });
          await getCandidatesWithDocuments({ preserveMissingSnapshot: true });
          toast.success(`${isCandidatePhoto ? 'Photo' : 'Document'} uploaded.`);
          return;
        }

        setDocumentUploadOutcomes((previousOutcomes) => {
          return {
            ...previousOutcomes,
            [documentCellKey]: createChecklistUploadOutcome({
              clientOperationId,
              file,
              isCandidatePhoto,
              replacementStatus,
            }),
          };
        });
        toast.error(getUploadOutcomeMessage({ isProfilePhoto: isCandidatePhoto, replacementStatus }));
      }
      checkLoginForError({ err: error, loginWithRedirect });
    } finally {
      if (isCurrentRequest()) {
        delete activeUploadRequestsRef.current[documentCellKey];
        setUploadingDocumentKeys((previousDocumentKeys) => {
          return previousDocumentKeys.filter((previousDocumentKey) => {
            return previousDocumentKey !== documentCellKey;
          });
        });
      }
    }
  };

  const candidateCountLabel = () => {
    const candidateLabel = visibleCandidates.length === 1 ? 'candidate' : 'candidates';
    const documentTypeName = selectedDocumentTypeDetails?.DocumentTypeName || 'selected document';

    if (documentFilter === 'missing') {
      return `${visibleCandidates.length} ${candidateLabel} missing ${documentTypeName}`;
    }

    if (documentFilter === 'present') {
      return `${visibleCandidates.length} ${candidateLabel} with ${documentTypeName}`;
    }

    return `${visibleCandidates.length} ${candidateLabel}`;
  };

  const viewCandidateDocument = (candidate, attachedDocument, isCandidatePhoto) => {
    if (!attachedDocument) {
      return;
    }

    if (isCandidatePhoto) {
      const photoWindow = window.open(attachedDocument.FullUrl, '_blank', 'noopener,noreferrer');
      if (photoWindow && photoWindow.focus) {
        photoWindow.focus();
      }
      return;
    }

    const documentDepartmentPk = attachedDocument.fk_Department || departmentPk;
    const departmentQuery = documentDepartmentPk ? `?pk_Department=${documentDepartmentPk}` : '';
    const documentWindow = window.open(
      `/viewFile/${candidate.pk_Candidate}/${attachedDocument.pk_File}/${attachedDocument.fk_DocumentType}/${attachedDocument.Name}${departmentQuery}`,
      '_blank',
      'noopener,noreferrer',
    );

    if (documentWindow && documentWindow.focus) {
      documentWindow.focus();
    }
  };

  const renderCoverageGrid = () => {
    if (isLoadingCandidates) {
      return <Loading options={{ labelText: 'Loading candidate documents. . .' }} />;
    }

    if (candidates.length <= 0) {
      return <div style={{ padding: 25, textAlign: 'center', border: '1px dashed #9BA7B5', color: '#5B677A' }}>No candidates found for this season.</div>;
    }

    return (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
          <div style={{ backgroundColor: '#E4F6E8', borderRadius: 4, color: '#146C2E', fontWeight: 'bold', padding: '7px 10px' }}>
            {coverageSummary.completeCandidateCount} complete
          </div>
          <div style={{ backgroundColor: '#FFF1E2', borderRadius: 4, color: '#A65E00', fontWeight: 'bold', padding: '7px 10px' }}>
            {coverageSummary.missingCandidateCount} missing one or more
          </div>
        </div>
        <div style={{ border: '1px solid #CED4DA', borderRadius: 4, maxHeight: 'calc(100vh - 450px)', overflow: 'auto' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 'max-content', width: '100%' }}>
            <thead>
              <tr>
                <th
                  style={{
                    backgroundColor: '#F4F6F8',
                    borderBottom: '1px solid #CED4DA',
                    left: 0,
                    minWidth: 220,
                    padding: '10px 12px',
                    position: 'sticky',
                    textAlign: 'left',
                    top: 0,
                    zIndex: 2,
                  }}
                >
                  Candidate
                </th>
                {visibleDocumentTypes.map((documentType) => {
                  return (
                    <th
                      key={documentType.pk_DocumentType}
                      style={{
                        backgroundColor: '#F4F6F8',
                        borderBottom: '1px solid #CED4DA',
                        minWidth: 150,
                        padding: '10px 12px',
                        position: 'sticky',
                        textAlign: 'center',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {documentType.DocumentTypeName || 'Unnamed Document Type'}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {candidates.map((candidate) => {
                const candidateName = `${candidate.LastName || ''}${candidate.LastName && candidate.FirstName ? ', ' : ''}${candidate.FirstName || ''}`;

                return (
                  <tr key={candidate.pk_Candidate}>
                    <td
                      style={{
                        backgroundColor: 'var(--rr-surface)',
                        borderBottom: '1px solid #E3E7EB',
                        fontWeight: 'bold',
                        left: 0,
                        padding: '10px 12px',
                        position: 'sticky',
                        zIndex: 1,
                      }}
                    >
                      {candidateName || 'Unnamed Candidate'}
                    </td>
                    {visibleDocumentTypes.map((documentType) => {
                      const documentTypePk = documentType.pk_DocumentType;
                      const attachedDocument = getDocumentForType(candidate, documentTypePk);

                      return (
                        <td
                          key={documentTypePk}
                          style={{ borderBottom: '1px solid #E3E7EB', padding: 6, textAlign: 'center' }}
                        >
                          <DocumentCoverageCell
                            attachedDocument={attachedDocument}
                            candidate={candidate}
                            documentType={documentType}
                            isUploading={uploadingDocumentKeys.includes(
                              getDocumentCellKey(candidate.pk_Candidate, documentTypePk),
                            )}
                            onRefresh={() => {
                              getCandidatesWithDocuments({ preserveMissingSnapshot: true });
                            }}
                            onRetry={() => {
                              const uploadOutcome = documentUploadOutcomes[
                                getDocumentCellKey(candidate.pk_Candidate, documentTypePk)
                              ];
                              uploadCandidateDocument(candidate, documentTypePk, uploadOutcome.file, uploadOutcome);
                            }}
                            onUpload={uploadCandidateDocument}
                            onViewDocument={viewCandidateDocument}
                            uploadOutcome={documentUploadOutcomes[
                              getDocumentCellKey(candidate.pk_Candidate, documentTypePk)
                            ]}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <div style={{ margin: 10, backgroundColor: 'var(--rr-surface)', width: '98%', minHeight: '60vh', padding: 15 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 15 }}>
        <div>
          <h4 style={{ marginBottom: 3 }}>Document Checklist</h4>
          <div style={{ color: '#5B677A', fontSize: 13 }}>Review document coverage for candidates in this season.</div>
        </div>
        <Button color="primary" disabled={isLoadingCandidates} onClick={getCandidatesWithDocuments}>
          <FontAwesomeIcon icon={faSyncAlt} spin={isLoadingCandidates} style={{ marginRight: 7 }} />
          Refresh
        </Button>
      </div>

      <div
        style={{
          border: '2px solid #0D6EFD',
          borderRadius: 8,
          display: 'inline-flex',
          marginBottom: 15,
          overflow: 'hidden',
        }}
      >
        {[
          { label: 'Checklist', value: 'checklist' },
          { label: 'Coverage Grid', value: 'coverage' },
        ].map((viewOption) => {
          const isSelected = viewMode === viewOption.value;

          return (
            <button
              aria-pressed={isSelected}
              key={viewOption.value}
              onClick={() => {
                setViewMode(viewOption.value);
              }}
              style={{
                backgroundColor: isSelected ? '#0D6EFD' : 'white',
                border: 'none',
                borderLeft: viewOption.value === 'checklist' ? 'none' : '1px solid #0D6EFD',
                color: isSelected ? 'white' : '#0D6EFD',
                cursor: 'pointer',
                fontWeight: isSelected ? 'bold' : 'normal',
                padding: '7px 14px',
              }}
              type="button"
            >
              {viewOption.label}
            </button>
          );
        })}
      </div>

      {viewMode === 'checklist' ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'end', gap: 15, marginBottom: 15 }}>
        <div style={{ minWidth: 260 }}>
          <label htmlFor="document_checklist_type" style={{ display: 'block', fontWeight: 'bold', marginBottom: 5 }}>
            Document Type
          </label>
          <Input
            id="document_checklist_type"
            type="select"
            value={selectedDocumentType}
            onChange={(event) => {
              const nextDocumentType = event.target.value;
              setSelectedDocumentType(nextDocumentType);
              resetFilterSnapshot(documentFilter, nextDocumentType);
            }}
            onClick={() => {
              resetFilterSnapshot(documentFilter);
            }}
          >
            <option value="">Select a document type</option>
            {visibleDocumentTypes.map((documentType) => {
              return (
                <option key={documentType.pk_DocumentType} value={documentType.pk_DocumentType}>
                  {documentType.DocumentTypeName || 'Unnamed Document Type'}
                </option>
              );
            })}
          </Input>
        </div>

        <div>
          <div
            style={{
              border: '2px solid #0D6EFD',
              borderRadius: 10,
              display: 'flex',
              overflow: 'hidden',
            }}
          >
            {[
              { label: 'Show all', value: 'all' },
              { label: 'Missing', value: 'missing' },
              { label: 'Has document', value: 'present' },
            ].map((filterOption) => {
              const isSelected = documentFilter === filterOption.value;

              return (
                <button
                  aria-pressed={isSelected}
                  key={filterOption.value}
                  onClick={() => {
                    if (!isSelected) {
                      setDocumentFilter(filterOption.value);
                      resetFilterSnapshot(filterOption.value);
                      return;
                    }

                    resetFilterSnapshot(filterOption.value);
                  }}
                  style={{
                    backgroundColor: isSelected ? '#0D6EFD' : 'white',
                    border: 'none',
                    borderLeft: filterOption.value === 'all' ? 'none' : '1px solid #0D6EFD',
                    color: isSelected ? 'white' : '#0D6EFD',
                    cursor: 'pointer',
                    fontSize: 16,
                    fontWeight: isSelected ? 'bold' : 'normal',
                    padding: '8px 18px',
                  }}
                  type="button"
                >
                  {filterOption.label}
                </button>
              );
            })}
          </div>
        </div>
          </div>

          {!selectedDocumentType ? (
            <div style={{ padding: 25, textAlign: 'center', border: '1px dashed #9BA7B5', color: '#5B677A' }}>
              Select a document type to view candidate document status.
            </div>
          ) : isLoadingCandidates ? (
            <Loading options={{ labelText: 'Loading candidate documents. . .' }} />
          ) : (
            <>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{candidateCountLabel()}</div>
              <div style={{ border: '1px solid #CED4DA', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 160px minmax(220px, 280px)', gap: 10, padding: '9px 12px', backgroundColor: '#F4F6F8', fontWeight: 'bold' }}>
                  <div>Candidate</div>
                  <div>{selectedDocumentTypeDetails?.DocumentTypeName || 'Document'} Status</div>
                  <div>Document</div>
                </div>
                <div style={{ maxHeight: 'calc(100vh - 540px)', overflowY: 'auto' }}>
                  {visibleCandidates.length <= 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#5B677A' }}>No candidates match this filter.</div>
                  ) : (
                    visibleCandidates.map((candidate) => {
                      return (
                        <DocumentChecklistRow
                          key={candidate.pk_Candidate}
                          attachedDocument={getDocumentForType(candidate, selectedDocumentType)}
                          candidate={candidate}
                          documentTypePk={selectedDocumentType}
                          documentTypeName={selectedDocumentTypeDetails?.DocumentTypeName || 'document'}
                          isCandidatePhoto={isCandidatePhotoType(selectedDocumentType)}
                          isJustAdded={recentlyUploadedPks.includes(candidate.pk_Candidate)}
                          isUploading={uploadingDocumentKeys.includes(
                            getDocumentCellKey(candidate.pk_Candidate, selectedDocumentType),
                          )}
                          onRefresh={() => {
                            getCandidatesWithDocuments({ preserveMissingSnapshot: true });
                          }}
                          onRetry={() => {
                            const uploadOutcome = documentUploadOutcomes[
                              getDocumentCellKey(candidate.pk_Candidate, selectedDocumentType)
                            ];
                            uploadCandidateDocument(candidate, selectedDocumentType, uploadOutcome.file, uploadOutcome);
                          }}
                          onUpload={uploadCandidateDocument}
                          onViewDocument={viewCandidateDocument}
                          uploadOutcome={documentUploadOutcomes[
                            getDocumentCellKey(candidate.pk_Candidate, selectedDocumentType)
                          ]}
                        />
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        renderCoverageGrid()
      )}
    </div>
  );
};

export default DocumentChecklist;
